import { FC, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { ApexLineChart, ApexPieChart } from '../../../../../PDFViewer/components/ApexChart';
import styles from './styles';
import { AppContext } from '../../../../../../context';
import moment from 'moment';
import ApexCharts from 'apexcharts';
import { client } from '../../../../../../App';
import { GET_AUTOMATED_COLUMNS } from '../../../../../../queries/widgets';
import { formatDecimalNumber, normalizeNumberFormatLocale, NumberFormatLocale } from '../../../../../../utils/numberFormat';
import { dynamicCellCacheKey, dynamicFieldCacheKey } from '../dynamicCacheKey';

export const DEFAULT_HISTORY_LEGEND_LABEL = '';
export const DEFAULT_FUND_PARTNER_LABELS = {
  custodian: 'Verwahrstelle',
  capitalManagement: 'Kapitalverwaltungsgesellschaft',
  fundAdvisor: 'Fondsberater',
};
/** Wait for ApexCharts to finish painting before dataURI capture (preview / publish). */
export const CHART_CAPTURE_RENDER_DELAY_MS = 600;

export const DEFAULT_IMPORTANT_NOTES = `Dies ist eine Werbemitteilung. Die in diesem Factsheet angegebenen Fondsinformationen sind zu allgemeinen Informationszwecken erstellt worden und dienen einer ersten Orientierung zu dem Produkt. Sie ersetzen weder eigene Marktrecherchen noch sonstige rechtliche, steuerliche oder finanzielle Informationen oder Beratung. Das Factsheet stellt keine Kauf- oder Verkaufsaufforderung oder Anlageberatung dar. Das Factsheet enthält nicht alle für wirtschaftlich bedeutende Entscheidungen wesentlichen Angaben und kann von Informationen und Einschätzungen anderer Quellen oder Marktteilnehmer abweichen.

Die vollständigen Angaben zu dem Fonds, einschließlich den mit einer Fondsanlage verbundenen Chancen und Risiken sind dem jeweils aktuellen Verkaufsprospekt nebst Verwaltungsreglement oder Satzung sowie ggf. dem Basisinformationsblatt (PRIIP-KIDs), ergänzt durch den letzten geprüften Jahresbericht bzw. den letzten Halbjahresbericht zu entnehmen. Diese Unterlagen stellen die alleinverbindliche Grundlage für den Kauf von Fondsanteilen dar. Sie sind in elektronischer oder gedruckter Form in deutscher Sprache kostenlos bei der Axxion S.A., 15 rue de Flaxweiler, L-6776 Grevenmacher erhältlich.

Der letzte Nettoinventarwert kann über die Internetseite der Axxion S.A. abgerufen werden.

Bitte beachten Sie: Wertentwicklungen in der Vergangenheit sind kein verlässlicher Indikator für die künftige Wertentwicklung. Investmentfonds unterliegen marktbedingten Kursschwankungen, die zu Verlusten, bis hin zum Totalverlust des eingesetzten Kapitals, führen können. Es wird ausdrücklich auf die Risikohinweise des Verkaufsprospektes verwiesen. Die zukünftige Wertentwicklung unterliegt der Besteuerung, die von der persönlichen Situation des jeweiligen Anlegers abhängig ist und sich in der Zukunft ändern kann.

Die Bruttowertentwicklung (BVI-Methode) berücksichtigt bereits alle auf Fondsebene anfallenden Kosten und geht von einer Wiederanlage eventueller Ausschüttung aus. Sofern nicht anders angegeben, entsprechen alle dargestellten Wertentwicklungen der Bruttowertentwicklung. Die Nettowertentwicklung geht von einer Modellrechnung mit einem investierten Betrag von 1.000 €, dem max. Ausgabeaufschlag sowie einem Rücknahmeabschlag (falls vorgesehen, siehe Stammdaten) aus. Sie berücksichtigt keine anderen eventuell anfallenden individuellen Kosten des Anlegers, wie beispielsweise eine Depotführungsgebühr.

Die ausgegebenen Anteile dieses Fonds dürfen nur in solchen Rechtsordnungen zum Kauf angeboten oder verkauft werden, in denen ein solches Angebot oder ein solcher Verkauf zulässig ist. So dürfen die Anteile dieses Fonds weder innerhalb der USA noch an oder für Rechnung von US Staatsbürgern oder in den USA ansässigen US-Personen zum Kauf angeboten oder an diese verkauft werden. Dieses Dokument und die in ihm enthaltenen Informationen dürfen nicht in den USA verbreitet werden. Die Verbreitung und Veröffentlichung dieses Dokumentes sowie das Angebot oder ein Verkauf der Anteile können auch in anderen Rechtsordnungen Beschränkungen unterworfen sein.

Eine Zusammenfassung der Anlegerrechte kann auf der Internetseite der Verwaltungsgesellschaft unter: https://www.axxion.lu/de/anlegerinformationen/ abgerufen werden. Die Axxion S.A. kann jederzeit beschließen, Vorkehrungen, die sie gegebenenfalls für den Vertrieb von Fondsanteilen getroffen hat, wieder aufzuheben.

Die in dem Factsheet enthaltenen Informationen sind für die Axxion S.A. und teilweise für Dritte (z.B. Morningstar) urheberrechtlich geschützt und dürfen nicht vervielfältigt oder verbreitet werden. Die in dem Factsheet enthaltenen Informationen und zum Ausdruck gebrachten Meinungen geben die Einschätzung zum Zeitpunkt der Veröffentlichung wieder und können sich jederzeit ohne vorherige Ankündigung ändern. Alle Angaben wurden mit großer Sorgfalt zusammengestellt. Für die Richtigkeit, Vollständigkeit oder Aktualität kann jedoch keine Gewähr übernommen werden.

Ein umfangreiches Fondslexikon zu Themen und Begriffen finden Sie unter: https://www.axxion.lu/de/fondslexikon/`;

export interface SectionHeaders {
  investmentApproach: string;
  top10Stocks: string;
  prices: string;
  conditions: string;
  masterData: string;
  performance: string;
  chart: string;
  countryAllocation: string;
  sectorAllocation: string;
  fundPartner: string;
  contact: string;
  importantNotes: string;
}

export const SECTION_HEADER_DEFAULTS: SectionHeaders = {
  investmentApproach: 'Investment-Ansatz',
  top10Stocks: 'Top 10 Aktien',
  prices: 'Preise',
  conditions: 'Konditionen',
  masterData: 'Stammdaten',
  performance: 'Performance (Annualized)',
  chart: 'Wertentwicklung seit Auflage',
  countryAllocation: 'Länderallokation',
  sectorAllocation: 'Sektorallokation',
  fundPartner: 'Fondspartner',
  contact: 'Kontakt',
  importantNotes: 'Wichtige Hinweise',
};

interface FactsheetPreviewProps {
  data?: any;
  title?: string;
  subtitle?: string;
  isPreviewStarted?: boolean;
  onChartImageChange?: (dataUrl?: string) => void;
  onPieChartImagesChange?: (countryChartUrl: string | undefined, sectorChartUrl: string | undefined) => void;
  onResetPreviewStarted: (value: boolean) => void;
  /** Parent calls this before preview/publish PDF to capture fully rendered charts. */
  onRegisterChartCapture?: (capture: () => Promise<void>) => void;
}

export const FactsheetPreview: FC<FactsheetPreviewProps> = ({
  data,
  title = 'Business Owner',
  subtitle = 'Ein Fonds für unternehmerisch denkende Anleger',
  isPreviewStarted,
  onChartImageChange,
  onPieChartImagesChange,
  onResetPreviewStarted,
  onRegisterChartCapture,
}) => {
  const { document } = useContext(AppContext);
  const effectiveData = data || {};
  const effectiveTitle = effectiveData.title || title;
  const effectiveSubtitle = effectiveData.subtitle || subtitle;
  const effectiveDate = effectiveData.data_as_of || effectiveData.published_at || 'September 2025';
  const numberFormatLocale: NumberFormatLocale = normalizeNumberFormatLocale(effectiveData.numberFormatLocale);
  const sectionHeaders: SectionHeaders = {
    ...SECTION_HEADER_DEFAULTS,
    ...(effectiveData.sectionHeaders || {}),
  };
  const historyLegendLabel: string = effectiveData.historyLegendLabel || '';
  const historyCurrencySymbol: string = effectiveData.historyCurrencySymbol || '€';
  const iconUrl = document?.template?.icon_url;

  const investmentApproach = effectiveData.investmentApproach || [
    'Wir investieren weltweit in börsennotierten Aktien aus nahezu allen Sektoren',
    'Wir suchen Unternehmen mit dauerhaften Wettbewerbsvorteilen geführt, die von Unternehmern geführt werden, welche ihr Lebenswerk aufbauen',
    'Wir kaufen, wenn der Preis im attraktiven Verhältnis zum Wert steht – oft in Phasen negativer Marktstimmung',
    'Wir investieren mit einem langfristigen Horizont und lassen Unternehmen Zeit, ihr Potenzial zu entfalten',
    'Wir denken unabhängig und handeln oft gegen den Konsens',
    'Wir investieren konzentriert in rund 10 sorgfältig ausgewählte Unternehmen, da exzellente Gelegenheiten selten sind',
    'Wir verstehen uns als engagierte Eigentümer und pflegen einen offenen, konstruktiven Dialog mit dem Management unserer Beteiligungen',
    'Wir informieren unsere Anleger regelmässig und transparent über unsere Philosophie, Portfoliostruktur und Anlageentscheidungen',
  ];

  const top10Stocks = effectiveData.top10Stocks || [
    'Alphabet Inc.', 'Berkshire Hathaway', 'Deutsche Post AG', 'Johnson & Johnson',
    'Microsoft Corp.', 'Nestlé SA', 'Novo Nordisk A/S', 'SAP SE',
    'UBS Group AG', 'Visa Inc.'
  ];

  // Get performance data - support both old and new structure
  const getPerformanceData = () => {
    const perf = effectiveData.performance;
    // New V1-style structure (labels/values/columns)
    if (perf && perf.labels && Array.isArray(perf.labels) && perf.values && Array.isArray(perf.values)) {
      return {
        labels: perf.labels,
        values: perf.values,
        columns: perf.columns || [],
      };
    }
    // Legacy structure (headers/rows)
    if (perf && perf.headers && perf.rows) {
      return {
        labels: perf.headers,
        values: perf.rows.map((row: any) => row.values || []),
        columns: [],
      };
    }
    // Old key-value structure - migrate on the fly
    if (perf && typeof perf === 'object' && !perf.headers && !perf.labels) {
      return {
        labels: ['YTD', '1 y', '3 y', '5 y', '10 y', 'since issue'],
        values: [[
          perf.ytd || '8,42%',
          perf['1j'] || '6,28%',
          perf['3j'] || '18,45%',
          perf['5j'] || '25,34%',
          perf['10j'] || '89,15%',
          perf.auflage || '165,0%'
        ]],
        columns: [],
      };
    }
    // Default
    return {
      labels: ['YTD', '1 y', '3 y', '5 y', '10 y', 'since issue'],
      values: [['8,42%', '6,28%', '18,45%', '25,34%', '89,15%', '165,0%']],
      columns: [],
    };
  };
  const performanceData = getPerformanceData();
  
  // State for fetched dynamic cell data
  const [cellDataCache, setCellDataCache] = useState<Map<string, any>>(new Map());
  const [loadingCells, setLoadingCells] = useState<Set<string>>(new Set());
  
  // Get ISIN for GraphQL queries
  const isin = document?.template?.isins?.[0] || effectiveData?.historyIsin || '';

  // Helper function to format date to YYYY-MM-DD for until parameter
  const formatDateToUntil = useCallback((dateStr: string | undefined): string | undefined => {
    if (!dateStr) return undefined;
    // If already YYYY-MM-DD, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    // Try to parse and format
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Axion snapshot date — not the PDF publication timestamp
  const until = formatDateToUntil(
    effectiveData?.data_as_of || effectiveData?.historyUntil || effectiveData?.published_at
  );

  const resolveDynamicIsin = useCallback((config: any): string | undefined => {
    if (isin) return isin;
    if (!config?.fields?.length) return undefined;
    const fieldWithIsin = config.fields.find((field: any) => typeof field?.isin === 'string' && field.isin.trim().length > 0);
    return fieldWithIsin?.isin?.trim();
  }, [isin]);

  // Fetch dynamic cell data using GraphQL
  const fetchCellData = useCallback(async (cell: any, rowIndex: number, colIndex: number) => {
    if (!cell?.isDynamic || !cell?.fields?.length) return null;
    const resolvedIsin = resolveDynamicIsin(cell);
    if (!resolvedIsin) return null;
    
    const cellKey = dynamicCellCacheKey(rowIndex, colIndex, until, numberFormatLocale);
    
    // Check cache first
    if (cellDataCache.has(cellKey)) {
      return cellDataCache.get(cellKey);
    }

    // Mark as loading
    setLoadingCells(prev => new Set(prev).add(cellKey));

    try {
      const res = await client.query({
        query: GET_AUTOMATED_COLUMNS,
        variables: { param: { data: [cell], isin: resolvedIsin, until, numberFormatLocale } },
      });
      
      const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
      
      // Cache the result - create a new Map to trigger re-render
      setCellDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(cellKey, fetchedValue);
        return newCache;
      });
      
      return fetchedValue;
    } catch (error) {
      console.error('Failed to fetch cell data:', error);
      return null;
    } finally {
      setLoadingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(cellKey);
        return newSet;
      });
    }
  }, [until, cellDataCache, resolveDynamicIsin, numberFormatLocale]);

  // Fetch dynamic value data using GraphQL
  const fetchDynamicValue = useCallback(async (section: string, field: string, value: any) => {
    if (!value?.isDynamic || !value?.fields?.length) return null;
    const resolvedIsin = resolveDynamicIsin(value);
    if (!resolvedIsin) return null;
    
    const valueKey = dynamicFieldCacheKey(section, field, until, numberFormatLocale);
    
    // Check cache first
    if (cellDataCache.has(valueKey)) {
      return cellDataCache.get(valueKey);
    }

    // Mark as loading
    setLoadingCells(prev => new Set(prev).add(valueKey));

    try {
      const res = await client.query({
        query: GET_AUTOMATED_COLUMNS,
        variables: { param: { data: [value], isin: resolvedIsin, until, numberFormatLocale } },
      });
      
      const fetchedValue = res.data.getAutomatedColumns?.[0]?.[0];
      
      // Cache the result
      setCellDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(valueKey, fetchedValue);
        return newCache;
      });
      
      return fetchedValue;
    } catch (error) {
      console.error('Failed to fetch dynamic value:', error);
      return null;
    } finally {
      setLoadingCells(prev => {
        const newSet = new Set(prev);
        newSet.delete(valueKey);
        return newSet;
      });
    }
  }, [until, cellDataCache, resolveDynamicIsin, numberFormatLocale]);

  useEffect(() => {
    setCellDataCache(new Map());
  }, [until, numberFormatLocale]);

  // Helper function to get display value (fetched or static)
  const getDisplayValue = useCallback((section: string, field: string, defaultValue: string): string => {
    const value = effectiveData[section]?.[field];
    
    // If it's a dynamic value, try to get from cache
    if (typeof value === 'object' && value?.isDynamic && value?.fields?.length) {
      const valueKey = dynamicFieldCacheKey(section, field, until, numberFormatLocale);
      const fetchedValue = cellDataCache.get(valueKey);
      if (fetchedValue !== undefined && fetchedValue !== null) {
        return String(fetchedValue);
      }
      // If not in cache yet, return default while loading
      return defaultValue;
    }
    
    // Return static value or default
    return typeof value === 'string' ? value : defaultValue;
  }, [effectiveData, cellDataCache, until, numberFormatLocale]);

  // Fetch all dynamic cells when performance data changes
  useEffect(() => {
    if (!performanceData.values || !Array.isArray(performanceData.values)) return;
    
    const fetchAllCells = async () => {
      const promises: Promise<void>[] = [];
      performanceData.values.forEach((row: any[], rowIndex: number) => {
        row.forEach((cell: any, colIndex: number) => {
          if (cell?.isDynamic && cell?.fields?.length) {
            promises.push(
              fetchCellData(cell, rowIndex, colIndex).then(() => undefined)
            );
          }
        });
      });
      await Promise.all(promises);
    };
    
    fetchAllCells();
  }, [isin, performanceData.values, fetchCellData, until, numberFormatLocale]);

  // Fetch all dynamic values when data changes
  useEffect(() => {
    const fetchAllDynamicValues = async () => {
      const promises: Promise<void>[] = [];
      
      // Fetch prices dynamic values
      ['nav', 'fundVolume'].forEach(field => {
        const value = effectiveData.prices?.[field];
        if (value?.isDynamic && value?.fields?.length) {
          promises.push(
            fetchDynamicValue('prices', field, value).then(() => undefined)
          );
        }
      });
      
      // Fetch conditions dynamic values
      ['issueSurcharge', 'redemptionFee', 'redemptionPeriod', 'ongoingCosts', 
       'managementFee', 'performanceFee', 'hurdleRate', 'highwaterMark', 
       'minSubscription', 'incomeUse'].forEach(field => {
        const value = effectiveData.conditions?.[field];
        if (value?.isDynamic && value?.fields?.length) {
          promises.push(
            fetchDynamicValue('conditions', field, value).then(() => undefined)
          );
        }
      });
      
      // Fetch masterData dynamic values
      ['isinWkn', 'fundCurrency', 'launchDate', 'initialPrice', 'fundDomicile'].forEach(field => {
        const value = effectiveData.masterData?.[field];
        if (value?.isDynamic && value?.fields?.length) {
          promises.push(
            fetchDynamicValue('masterData', field, value).then(() => undefined)
          );
        }
      });
      
      await Promise.all(promises);
    };
    
    fetchAllDynamicValues();
  }, [isin, effectiveData.prices, effectiveData.conditions, effectiveData.masterData, fetchDynamicValue, until, numberFormatLocale]);

  const pricesData = {
    nav: getDisplayValue('prices', 'nav', '1.419,31 EUR'),
    fundVolume: getDisplayValue('prices', 'fundVolume', '507.296.946 EUR')
  };

  const conditionsData = {
    issueSurcharge: getDisplayValue('conditions', 'issueSurcharge', '0%'),
    redemptionFee: getDisplayValue('conditions', 'redemptionFee', '0%'),
    redemptionPeriod: getDisplayValue('conditions', 'redemptionPeriod', '40 Tage zum Quartalsende'),
    ongoingCosts: getDisplayValue('conditions', 'ongoingCosts', '1,09%'),
    managementFee: getDisplayValue('conditions', 'managementFee', '1,00%'),
    performanceFee: getDisplayValue('conditions', 'performanceFee', '10%'),
    hurdleRate: getDisplayValue('conditions', 'hurdleRate', '6% p.a.'),
    highwaterMark: getDisplayValue('conditions', 'highwaterMark', 'Ja'),
    minSubscription: getDisplayValue('conditions', 'minSubscription', '200.000 EUR'),
    incomeUse: getDisplayValue('conditions', 'incomeUse', 'Thesaurierend')
  };

  const masterData = {
    isinWkn: getDisplayValue('masterData', 'isinWkn', 'DE000A0RAAR6 / A0RAAR'),
    fundCurrency: getDisplayValue('masterData', 'fundCurrency', 'EUR'),
    launchDate: getDisplayValue('masterData', 'launchDate', '30.09.2008'),
    initialPrice: getDisplayValue('masterData', 'initialPrice', '100 EUR'),
    fundDomicile: getDisplayValue('masterData', 'fundDomicile', 'Deutschland')
  };
  const lineChartData = effectiveData.lineChartData || [];
  const countryAlloc = effectiveData.countryAllocation || [
    { label: 'Deutschland', value: 35 },
    { label: 'USA', value: 25 },
    { label: 'Schweiz', value: 15 },
    { label: 'Österreich', value: 12 },
    { label: 'Niederlande', value: 8 },
    { label: 'Sonstige', value: 5 },
  ];
  const sectorAlloc = effectiveData.sectorAllocation || [
    { label: 'Technologie', value: 28 },
    { label: 'Finanzdienstleistungen', value: 22 },
    { label: 'Gesundheitswesen', value: 18 },
    { label: 'Industrie', value: 15 },
    { label: 'Konsumgüter', value: 12 },
    { label: 'Sonstige', value: 5 },
  ];

  const fundPartner = effectiveData.fundPartner || {
    custodian: 'DZ Bank AG',
    capitalManagement: 'Axxion S.A.',
    fundAdvisor: 'RV Capital AG'
  };
  const fundPartnerLabels = effectiveData.fundPartnerLabels || DEFAULT_FUND_PARTNER_LABELS;

  const contact = effectiveData.contact || {
    companyName: 'RV Capital AG',
    address: 'Einsiedlerstrasse 23, 8834 Schindellegi, Schweiz',
    email: 'info@rvcapital.ch',
    website: 'www.rvcapital.ch'
  };

  const importantNotes = effectiveData.importantNotes || DEFAULT_IMPORTANT_NOTES;

  const lineChartDataRef = useRef(lineChartData);
  lineChartDataRef.current = lineChartData;

  const captureHistoryCharts = useCallback(async (cancelled: boolean) => {
    try {
      await new Promise((r) => setTimeout(r, CHART_CAPTURE_RENDER_DELAY_MS));

      let historyChartUrl: string | undefined;
      const series = lineChartDataRef.current;

      if (series && series.length > 0) {
        try {
          const res: any = await (ApexCharts as any).exec('area-datetime', 'dataURI', { scale: 3 });
          historyChartUrl = res?.imgURI;
        } catch (e) {
          console.error('Error capturing history chart:', e);
        }
      }

      if (!cancelled) {
        onChartImageChange?.(historyChartUrl);
      }
    } catch (e) {
      console.error('Error in history chart capture:', e);
      if (!cancelled) {
        onChartImageChange?.(undefined);
      }
    }
  }, [onChartImageChange]);

  const capturePieCharts = useCallback(async (cancelled: boolean) => {
    try {
      await new Promise((r) => setTimeout(r, CHART_CAPTURE_RENDER_DELAY_MS));

      let countryChartUrl: string | undefined;
      let sectorChartUrl: string | undefined;

      if (countryAlloc && countryAlloc.length > 0) {
        try {
          const countryRes: any = await (ApexCharts as any).exec('country-allocation-chart', 'dataURI', { scale: 3 });
          countryChartUrl = countryRes?.imgURI;
        } catch (e) {
          console.error('Error capturing country chart:', e);
        }
      }

      if (sectorAlloc && sectorAlloc.length > 0) {
        try {
          const sectorRes: any = await (ApexCharts as any).exec('sector-allocation-chart', 'dataURI', { scale: 3 });
          sectorChartUrl = sectorRes?.imgURI;
        } catch (e) {
          console.error('Error capturing sector chart:', e);
        }
      }

      if (!cancelled) {
        onPieChartImagesChange?.(countryChartUrl, sectorChartUrl);
      }
    } catch (e) {
      console.error('Error in pie chart capture:', e);
      if (!cancelled) {
        onPieChartImagesChange?.(undefined, undefined);
      }
    }
  }, [countryAlloc, sectorAlloc, onPieChartImagesChange]);

  const captureAllChartsForExport = useCallback(async () => {
    await captureHistoryCharts(false);
    await capturePieCharts(false);
  }, [captureHistoryCharts, capturePieCharts]);

  useEffect(() => {
    if (!onRegisterChartCapture) return;
    onRegisterChartCapture(captureAllChartsForExport);
  }, [onRegisterChartCapture, captureAllChartsForExport]);

  useEffect(() => {
    let cancelled = false;
    const capture = async () => {
      await captureHistoryCharts(cancelled);
    };
    capture();
    return () => { cancelled = true; };
  }, [lineChartData, historyLegendLabel, historyCurrencySymbol, captureHistoryCharts]);

  // Capture pie charts
  useEffect(() => {
    let cancelled = false;

    capturePieCharts(cancelled);
    return () => { cancelled = true; };
  }, [countryAlloc, sectorAlloc]);

  useEffect(() => {
    if (isPreviewStarted) {
      (async () => {
        await captureHistoryCharts(false);
        await capturePieCharts(false);
        onResetPreviewStarted(false);
      })();
    }
  }, [isPreviewStarted]);

  return (
    <>
      <Box sx={styles.container}>
        <Box id="header-section" sx={styles.header}>
          <Box sx={styles.headerLeft}>
            <Typography sx={styles.title}>{effectiveTitle}</Typography>
            <Typography sx={styles.italicSub}>{effectiveSubtitle}</Typography>
          </Box>
          <Box sx={styles.headerRight}>
            {iconUrl ? (
              <img src={iconUrl} alt="logo" style={{ height: 44 }} />
            ) : (
              <Typography sx={styles.logo}>Rv Capital</Typography>
            )}
            <Box sx={styles.dateBadge}>{moment(effectiveDate, ['YYYY-MM-DD', 'YYYY-MM']).format('DD.MM.YYYY')}</Box>
          </Box>
        </Box>

        <Box sx={styles.body}>
          <Box sx={styles.twoCol}>
            {/* LEFT COLUMN */}
            <Box>
              <Box id="investment-approach-section" sx={styles.sectionToned}>
                <Typography sx={styles.sectionHeader}>{sectionHeaders.investmentApproach}</Typography>
                <Box component="ul" sx={styles.bulletList as any}>
                  {investmentApproach.map((point: string, index: number) => (
                    <Box key={index} component="li">{point}</Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mt: 16 }} />
              <Box id="top10-stocks-section" sx={styles.sectionToned}>
                <Typography sx={styles.sectionHeader}>{sectionHeaders.top10Stocks}</Typography>
                <Box sx={styles.top10Box}>
                  <Box sx={styles.top10Columns}>
                    {top10Stocks.map((stock: string, index: number) => (
                      <Box key={index} sx={styles.top10Item}>{stock}</Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* RIGHT COLUMN */}
            <Box>
              <Box id="prices-section" sx={styles.sectionToned}>
                <Typography sx={styles.sectionHeader}>{sectionHeaders.prices}</Typography>
                <Box sx={styles.kvGroup}>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Net Asset Value</Typography><Typography sx={styles.kvValue}>{pricesData.nav}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Assets under Management</Typography><Typography sx={styles.kvValue}>{pricesData.fundVolume}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Minimum investment</Typography><Typography sx={styles.kvValue}>{conditionsData.minSubscription}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Divided policy</Typography><Typography sx={styles.kvValue}>{conditionsData.incomeUse}</Typography></Box>
                </Box>
              </Box>

              <Box sx={{ mt: 16 }} />
              <Box id="conditions-section" sx={styles.sectionToned}>
                <Typography sx={styles.sectionHeader}>{sectionHeaders.conditions}</Typography>
                <Box sx={styles.kvGroup}>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Initial Sales Fee</Typography><Typography sx={styles.kvValue}>{conditionsData.issueSurcharge}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Redemption fee</Typography><Typography sx={styles.kvValue}>{conditionsData.redemptionFee}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Redemption notice period</Typography><Typography sx={styles.kvValue}>{conditionsData.redemptionPeriod}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Total Expense Ratio</Typography><Typography sx={styles.kvValue}>{conditionsData.ongoingCosts}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>of which management fee</Typography><Typography sx={styles.kvValue}>{conditionsData.managementFee}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Performance fee</Typography><Typography sx={styles.kvValue}>{conditionsData.performanceFee}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Hurdle Rate</Typography><Typography sx={styles.kvValue}>{conditionsData.hurdleRate}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Highwater Mark</Typography><Typography sx={styles.kvValue}>{conditionsData.highwaterMark}</Typography></Box>
                </Box>
              </Box>

              <Box sx={{ mt: 16 }} />
              <Box id="master-data-section" sx={styles.sectionToned}>
                <Typography sx={styles.sectionHeader}>{sectionHeaders.masterData}</Typography>
                <Box sx={styles.kvGroup}>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>ISIN / WKN</Typography><Typography sx={styles.kvValue}>{masterData.isinWkn}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Fund currency</Typography><Typography sx={styles.kvValue}>{masterData.fundCurrency}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Inception date</Typography><Typography sx={styles.kvValue}>{masterData.launchDate}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Initial issue price</Typography><Typography sx={styles.kvValue}>{masterData.initialPrice}</Typography></Box>
                  <Box sx={styles.kvRow}><Typography sx={styles.kvLabel}>Fund domicile</Typography><Typography sx={styles.kvValue}>{masterData.fundDomicile}</Typography></Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 16 }} />
          <Box id="performance-section" sx={styles.sectionToned}>
            <Typography sx={styles.sectionHeader}>{sectionHeaders.performance}</Typography>
            <Box sx={styles.perfTable}>
              <Box sx={{
                ...styles.perfHeader,
                gridTemplateColumns: `2fr repeat(${performanceData.labels.length - 1}, 1fr)`
              }}>
                {performanceData.labels.map((label, index) => (
                  <Box key={index} sx={index === 0 ? styles.perfCellHeaderTimeline : styles.perfCellHeader}>{label}</Box>
                ))}
              </Box>
              {performanceData.values.map((row, rowIndex) => (
                <Box 
                  key={rowIndex} 
                  sx={{
                    ...styles.perfRow,
                    gridTemplateColumns: `2fr repeat(${performanceData.labels.length - 1}, 1fr)`
                  }}
                >
                  {row.map((cell: any, colIndex: number) => {
                    const columnType = performanceData.columns?.[colIndex]?.type || 'text';
                    const isDynamic = cell?.isDynamic && cell?.fields?.length;
                    const cellKey = dynamicCellCacheKey(rowIndex, colIndex, until, numberFormatLocale);
                    const fetchedValue = isDynamic ? cellDataCache.get(cellKey) : null;
                    
                    let displayValue = '';
                    if (isDynamic && fetchedValue !== undefined && fetchedValue !== null) {
                      // Use fetched value from GraphQL
                      displayValue = String(fetchedValue);
                    } else if (columnType === 'text') {
                      displayValue = typeof cell === 'string' ? cell : (cell?.text || '');
                    } else if (columnType === 'link') {
                      displayValue = cell?.linkText || '';
                    } else if (columnType === 'avatar') {
                      displayValue = typeof cell === 'string' ? cell : '';
                    }
                    return (
                      <Box key={colIndex} sx={{ 
                        ...(colIndex === 0 ? styles.perfCellTimeline : styles.perfCell), 
                        ...styles.perfPositive 
                      }}>
                        {columnType === 'avatar' && displayValue ? (
                          <Box component="img" src={displayValue} sx={{ width: 32, height: 32, borderRadius: '50%' }} />
                        ) : columnType === 'link' && cell?.linkUrl ? (
                          <Box component="a" href={cell.linkUrl} target="_blank" rel="noopener noreferrer" sx={{ color: 'inherit', textDecoration: 'underline' }}>
                            {displayValue}
                          </Box>
                        ) : (
                          displayValue
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mt: 16 }} />
          <Box id="chart-section" sx={styles.sectionToned}>
            <Typography sx={styles.sectionHeader}>{sectionHeaders.chart}</Typography>
          <Box sx={styles.historyChart}>
            <ApexLineChart
              data={lineChartData}
              isSolActive={!!document?.template?.isins?.length}
              color={document?.template?.color}
              loading={false}
              showToolbar={false}
              fillType="solid"
              solidFillOpacity={0.15}
              height={225}
              background="transparent"
              seriesName={historyLegendLabel}
              yAxisPrefix=""
              yAxisFormatter={(value: number) => {
                const rounded = Math.round(value);
                const formatted = Number.isFinite(rounded)
                  ? formatDecimalNumber(rounded, numberFormatLocale, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                  : String(value);
                return `${formatted} ${historyCurrencySymbol || ''}`.trim();
              }}
            />
          </Box>
          </Box>

          <Box sx={{ mt: 16 }} />

          <Box id="fund-partner-section" sx={styles.gridBorderedSection}>
            <Box>
              <Typography sx={styles.sectionHeader}>{sectionHeaders.fundPartner}</Typography>
              <Box>
                <Box sx={styles.fundPartnerItem}>
                  <Typography sx={styles.fundPartnerLabel}>{fundPartnerLabels.custodian || DEFAULT_FUND_PARTNER_LABELS.custodian}:</Typography>
                  <Typography sx={styles.fundPartnerValue}>{fundPartner.custodian}</Typography>
                </Box>
                <Box sx={styles.fundPartnerItem}>
                  <Typography sx={styles.fundPartnerLabel}>{fundPartnerLabels.capitalManagement || DEFAULT_FUND_PARTNER_LABELS.capitalManagement}:</Typography>
                  <Typography sx={styles.fundPartnerValue}>{fundPartner.capitalManagement}</Typography>
                </Box>
                <Box sx={styles.fundPartnerItem}>
                  <Typography sx={styles.fundPartnerLabel}>{fundPartnerLabels.fundAdvisor || DEFAULT_FUND_PARTNER_LABELS.fundAdvisor}:</Typography>
                  <Typography sx={styles.fundPartnerValue}>{fundPartner.fundAdvisor}</Typography>
                </Box>
              </Box>
            </Box>
            <Box id="contact-section">
              <Typography sx={styles.sectionHeader}>{sectionHeaders.contact}</Typography>
              <Box>
                <Typography sx={styles.contactItem}>{contact.companyName}</Typography>
                <Typography sx={styles.contactItem}>{contact.address}</Typography>
                <Typography sx={styles.contactItem}>{contact.email}</Typography>
                <Typography sx={styles.contactItem}>{contact.website}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={styles.container}>
        <Box sx={styles.body}>
          {/* <Box id="pie-section" sx={styles.gridMd}>
            <Box sx={styles.pieSection}>
              <Typography sx={styles.sectionHeader}>{sectionHeaders.countryAllocation}</Typography>
              <ApexPieChart
                id="country-allocation-chart"
                data={countryAlloc}
                height={300}
                background="transparent"
              />
            </Box>
            <Box id="sector-allocation-section" sx={styles.pieSection}>
              <Typography sx={styles.sectionHeader}>{sectionHeaders.sectorAllocation}</Typography>
              <ApexPieChart
                id="sector-allocation-chart"
                data={sectorAlloc}
                height={300}
                background="transparent"
              />
            </Box>
          </Box> */}

          <Box sx={{ mt: 16 }}>
            <Typography sx={styles.sectionHeader}>
              {sectionHeaders.importantNotes}
            </Typography>

            <Typography sx={styles.sectionText} component="div">
              {importantNotes.split('\n\n').map((paragraph: string, index: number) => (
                <span key={index}>
                  {paragraph}
                  {index < importantNotes.split('\n\n').length - 1 && <><br /><br /></>}
                </span>
              ))}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default FactsheetPreview;


